import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  TrendingUp,
  Target,
  Lightbulb,
  Sparkles,
  CheckCircle,
  Clock,
  ArrowRight,
  BarChart3,
  BookOpen,
  Zap,
  Calendar,
  Trophy,
  Brain,
  MessageSquare
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { getTipOfTheDay } from '../data/storytellingTips';
import { getChallengeOfTheWeek } from '../data/storytellingChallenges';
import { getAllModules } from '../data/modules';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { assessmentResults, moduleProgress, toolsUsage } = useApp();

  // Get daily tip and weekly challenge
  const tipOfDay = getTipOfTheDay();
  const weeklyChallenge = getChallengeOfTheWeek();

  // Track assessment history for growth comparison
  // In a real app, this would come from AppContext/Firebase
  // For now, we'll check if baseline exists to show growth
  const hasBaselineAssessment = assessmentResults?.baseline;
  const hasCurrentAssessment = assessmentResults?.dimensions;

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const moduleIds = Object.keys(moduleProgress || {});
    if (moduleIds.length === 0) return 0;
    const completedCount = moduleIds.filter(id => moduleProgress[id]?.completed).length;
    return Math.round((completedCount / 8) * 100); // Assuming 8 modules total
  };

  // Calculate tool usage stats
  const calculateToolStats = () => {
    const toolIds = Object.keys(toolsUsage || {});
    const totalUses = toolIds.reduce((sum, id) => sum + (toolsUsage[id]?.uses || 0), 0);
    return {
      toolsExplored: toolIds.length,
      totalUses
    };
  };

  const toolStats = calculateToolStats();
  const overallProgress = calculateOverallProgress();

  // Calculate challenge stats across all modules
  const calculateChallengeStats = () => {
    const allModules = getAllModules();
    let totalChallenges = 0;
    let completedChallenges = 0;
    const recentChallenges = [];

    allModules.forEach(module => {
      const challenges = module.conversationChallenges || [];
      totalChallenges += challenges.length;

      const moduleProgressData = moduleProgress?.[module.id];
      const completedChallengeIndices = moduleProgressData?.completedChallenges || [];

      completedChallenges += completedChallengeIndices.length;

      // Collect recent uncompleted challenges
      challenges.forEach((challenge, idx) => {
        if (!completedChallengeIndices.includes(idx)) {
          recentChallenges.push({
            ...challenge,
            moduleId: module.id,
            moduleTitle: module.title,
            challengeIndex: idx
          });
        }
      });
    });

    return {
      totalChallenges,
      completedChallenges,
      completionPercentage: totalChallenges > 0 ? Math.round((completedChallenges / totalChallenges) * 100) : 0,
      recentChallenges: recentChallenges.slice(0, 3) // Show top 3 incomplete challenges
    };
  };

  const challengeStats = calculateChallengeStats();

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'Intermediate': return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
      case 'Advanced': return 'text-red-400 bg-red-400/10 border-red-400/30';
      default: return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-4 md:pb-8 pt-40">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section with Icon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-block"
          >
            <img
              src="/images/dashboard.png"
              alt="Your Dashboard"
              className="w-44 h-44 object-contain rounded-2xl mx-auto"
            />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            Your Storytelling Journey
          </h1>
          <p className="text-xl text-slate-200">
            Track your growth as a storyteller
          </p>
        </motion.div>

        {/* Storytelling Assessment Summary */}
        {assessmentResults ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                      {assessmentResults.profileType?.name || 'Your Storytelling Profile'}
                    </h2>
                    <p className="text-purple-200">
                      {assessmentResults.profileType?.tagline || `Overall Score: ${assessmentResults.avgScore || 0}/100`}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/assessment')}
                >
                  Retake
                </Button>
              </div>

              {/* 7 Dimensions Visualization */}
              {assessmentResults.dimensions && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-purple-300 uppercase mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Your Storytelling Dimensions
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(assessmentResults.dimensions).map(([key, dimension]) => (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{dimension.icon}</span>
                            <span className="text-slate-200 font-medium">{dimension.name}</span>
                          </div>
                          <span className="text-purple-300 font-bold">{dimension.score}/100</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all"
                            style={{ width: `${dimension.score}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Strengths & Growth Areas */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Strengths */}
                <div>
                  <h3 className="text-sm font-semibold text-purple-300 uppercase mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Your Strengths
                  </h3>
                  <div className="space-y-2">
                    {assessmentResults.profileType?.strengths?.slice(0, 3).map((strength, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {strength}
                      </div>
                    )) || <p className="text-slate-400 text-sm">Complete assessment to see strengths</p>}
                  </div>
                </div>

                {/* Growth Areas */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-300 uppercase mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Growth Opportunities
                  </h3>
                  <div className="space-y-2">
                    {assessmentResults.profileType?.growthAreas?.slice(0, 3).map((area, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                        <TrendingUp className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        {area}
                      </div>
                    )) || <p className="text-slate-400 text-sm">Complete assessment to see growth areas</p>}
                  </div>
                </div>
              </div>

              {/* Recommended Path */}
              {assessmentResults.profileType?.nextSteps && assessmentResults.profileType.nextSteps.length > 0 && (
                <div className="pt-6 border-t border-purple-500/20">
                  <h3 className="text-sm font-semibold text-white uppercase mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Recommended Next Steps
                  </h3>
                  <div className="space-y-2">
                    {assessmentResults.profileType.nextSteps.slice(0, 2).map((step, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                        <div className="w-6 h-6 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <p className="text-slate-300 text-sm flex-1">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="p-8 text-center bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
              <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Discover Your Storytelling DNA
              </h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                Take the Storytelling Style Profile to understand your natural storytelling strengths, identify growth opportunities, and establish your baseline for tracking progress over time.
              </p>
              <div className="flex justify-center">
                <Button
                  onClick={() => navigate('/assessment')}
                  className="inline-flex items-center gap-2"
                >
                  <span>Take Assessment</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Growth Tracking - Shows when user has baseline and current assessment */}
        {hasBaselineAssessment && hasCurrentAssessment && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8"
          >
            <Card className="p-6 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border-emerald-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Your Growth Journey</h2>
                  <p className="text-sm text-slate-400">
                    Comparing your baseline to current storytelling skills
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Baseline vs Current Comparison */}
                <div>
                  <h3 className="text-sm font-semibold text-emerald-300 uppercase mb-4">
                    Dimension Growth
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(assessmentResults.dimensions).slice(0, 4).map(([key, dimension]) => {
                      const baselineScore = assessmentResults.baseline?.dimensions?.[key]?.score || 0;
                      const currentScore = dimension.score;
                      const growth = currentScore - baselineScore;
                      const growthPercentage = baselineScore > 0 ? Math.round((growth / baselineScore) * 100) : 0;

                      return (
                        <div key={key} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span>{dimension.icon}</span>
                            <span className="text-slate-300">{dimension.name}</span>
                          </div>
                          {growth > 0 ? (
                            <div className="flex items-center gap-1 text-emerald-400">
                              <TrendingUp className="w-3 h-3" />
                              <span className="font-bold">+{growth}</span>
                              <span className="text-xs text-slate-500">({growthPercentage > 0 ? `+${growthPercentage}%` : '0%'})</span>
                            </div>
                          ) : growth < 0 ? (
                            <div className="flex items-center gap-1 text-amber-400">
                              <span className="font-bold">{growth}</span>
                              <span className="text-xs text-slate-500">({growthPercentage}%)</span>
                            </div>
                          ) : (
                            <span className="text-slate-500 text-xs">No change</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Growth Insights */}
                <div>
                  <h3 className="text-sm font-semibold text-teal-300 uppercase mb-4">
                    Growth Highlights
                  </h3>
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-semibold text-white">Biggest Improvement</span>
                      </div>
                      <p className="text-xs text-slate-400">
                        {/* Calculate biggest improvement - this is placeholder logic */}
                        Keep working through the Dive In modules to track your growth
                      </p>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Target className="w-4 h-4 text-teal-400" />
                        <span className="text-sm font-semibold text-white">Next Focus Area</span>
                      </div>
                      <p className="text-xs text-slate-400">
                        Continue building on your momentum by completing recommended modules
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-emerald-500/20">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    <strong className="text-emerald-400">Pro tip:</strong> Retake the assessment every 4-6 weeks to track your storytelling growth over time
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate('/assessment')}
                  >
                    Retake Assessment
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-4 gap-4 mb-8"
        >
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-8 h-8 text-blue-400" />
              <span className="text-slate-400 text-sm">Overall Progress</span>
            </div>
            <div className="text-3xl font-bold text-white">{overallProgress}%</div>
            <div className="mt-3 w-full bg-slate-700 rounded-full h-2">
              <div
                className="h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="w-8 h-8 text-green-400" />
              <span className="text-slate-400 text-sm">Modules Started</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {Object.keys(moduleProgress || {}).length}
            </div>
            <p className="text-sm text-slate-500 mt-1">of 8 total</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Zap className="w-8 h-8 text-amber-400" />
              <span className="text-slate-400 text-sm">Tools Used</span>
            </div>
            <div className="text-3xl font-bold text-white">{toolStats.totalUses}</div>
            <p className="text-sm text-slate-500 mt-1">{toolStats.toolsExplored} tools explored</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-8 h-8 text-purple-400" />
              <span className="text-slate-400 text-sm">Completed</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {Object.values(moduleProgress || {}).filter(m => m.completed).length}
            </div>
            <p className="text-sm text-slate-500 mt-1">modules finished</p>
          </Card>
        </motion.div>

        {/* Real-World Challenges Section */}
        {challengeStats.totalChallenges > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <Card className="p-6 bg-gradient-to-br from-indigo-500/5 to-violet-500/5 border-indigo-500/20">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Storytelling Challenges</h2>
                    <p className="text-sm text-slate-400">
                      Practise your storytelling in real-world contexts
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-white">
                    {challengeStats.completedChallenges}/{challengeStats.totalChallenges}
                  </div>
                  <p className="text-sm text-slate-400">completed</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-300">Overall Progress</span>
                  <span className="text-sm font-bold text-indigo-300">{challengeStats.completionPercentage}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div
                    className="h-3 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all"
                    style={{ width: `${challengeStats.completionPercentage}%` }}
                  />
                </div>
              </div>

              {/* Challenge Cards */}
              {challengeStats.recentChallenges.length > 0 ? (
                <>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-indigo-400" />
                    Upcoming Challenges
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {challengeStats.recentChallenges.map((challenge, idx) => (
                      <div
                        key={idx}
                        className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-indigo-500/50 transition-all cursor-pointer group"
                        onClick={() => navigate(`/modules/${challenge.moduleId}`)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors">
                            <MessageSquare className="w-5 h-5 text-indigo-400" />
                          </div>
                          <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded">
                            {challenge.moduleTitle}
                          </span>
                        </div>
                        <h4 className="text-white font-semibold mb-2 group-hover:text-indigo-300 transition-colors">
                          {challenge.scenario}
                        </h4>
                        <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                          {challenge.challenge}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-indigo-400">
                          <ArrowRight className="w-3 h-3" />
                          <span>Go to module</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">All Challenges Complete!</h3>
                  <p className="text-slate-400">
                    Amazing work! You've completed all available challenges.
                  </p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-indigo-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Lightbulb className="w-4 h-4 text-amber-400" />
                  <span>Complete challenges to reinforce your learning</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/modules')}
                  className="text-indigo-400 hover:text-indigo-300"
                >
                  View All Modules
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Tip of the Day & Weekly Challenge */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Tip of the Day */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 h-full bg-gradient-to-br from-teal-500/5 to-blue-500/5 border-teal-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center">
                  <Lightbulb className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Today's Storytelling Tip</h3>
                  <span className="text-xs text-teal-300">{tipOfDay.category}</span>
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-slate-200 leading-relaxed">{tipOfDay.tip}</p>
                <div className="pt-3 border-t border-teal-500/20">
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-teal-300">Try this: </span>
                    {tipOfDay.actionable}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Weekly Challenge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 h-full bg-gradient-to-br from-orange-500/5 to-red-500/5 border-orange-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">This Week's Challenge</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-orange-300">{weeklyChallenge.category}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getDifficultyColor(weeklyChallenge.difficulty)}`}>
                        {weeklyChallenge.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 font-bold">{weeklyChallenge.points} pts</span>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-white">{weeklyChallenge.title}</h4>
                <p className="text-slate-200 text-sm leading-relaxed">{weeklyChallenge.description}</p>
                <div className="pt-3 border-t border-orange-500/20">
                  <p className="text-sm text-slate-300">
                    <span className="font-semibold text-orange-300">Why it matters: </span>
                    {weeklyChallenge.why}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span>{weeklyChallenge.timeCommitment}</span>
                </div>
                <Button size="sm" variant="secondary" className="w-full mt-2">
                  Accept Challenge
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/modules')}
            >
              <BookOpen className="w-10 h-10 text-blue-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Continue Learning</h3>
              <p className="text-sm text-slate-400">
                Pick up where you left off in your modules
              </p>
              <ArrowRight className="w-5 h-5 text-blue-400 mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/toolkit')}
            >
              <Zap className="w-10 h-10 text-amber-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Use Tools</h3>
              <p className="text-sm text-slate-400">
                Practise with the storytelling tools
              </p>
              <ArrowRight className="w-5 h-5 text-amber-400 mt-3" />
            </Card>

            <Card
              className="p-6 cursor-pointer hover:scale-105 transition-transform"
              onClick={() => navigate('/resources')}
            >
              <MessageSquare className="w-10 h-10 text-purple-400 mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Explore Resources</h3>
              <p className="text-sm text-slate-400">
                Books, courses, research papers, podcasts and videos
              </p>
              <ArrowRight className="w-5 h-5 text-purple-400 mt-3" />
            </Card>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
